import { Pencil, Pin, PinOff, Trash2 } from 'lucide-react';
import { useTranslation } from '../../../lib/i18n';
import type { Conversation } from '../../../lib/conversations';
import type { ConversationActionsFlow } from '../../../lib/use-conversation-actions';
import { OverflowMenu } from '../ui';

/**
 * The 3-dot menu on a conversation row: Pin/Unpin, Rename and Delete.
 *
 * One component for every list that shows conversations — the wide rail's Chat History, the mobile drawer,
 * the search dialog — so the actions, their order, their glyphs and their labels are identical wherever a
 * thread appears. A row menu that offers different things on different screens is the drift this exists to
 * prevent.
 *
 * The pin item is a TOGGLE that names the outcome ("Pin" / "Unpin") rather than the state, so the label
 * always describes what pressing it will do. Delete sits last and alone in the destructive skin, so the one
 * item that removes something is never adjacent to the one that is pressed most.
 */
export function ConversationRowMenu({
  conv,
  title,
  flow,
}: {
  conv: Conversation;
  title: string;
  flow: ConversationActionsFlow;
}) {
  const { t } = useTranslation();
  const pinned = Boolean(conv.pinned);

  // A row with no server id cannot be pinned or deleted — both writes address the conversation by id. Better
  // to offer no menu than a menu whose every item is a silent no-op. This also narrows `conv.id` to a string
  // for the calls below.
  const id = conv.id;
  if (!id) return null;

  return (
    <OverflowMenu
      ariaLabel={t('conversations.rowMenu.label', { title })}
      actions={[
        {
          key: 'pin',
          label: pinned ? t('conversations.unpin') : t('conversations.pin'),
          icon: pinned ? <PinOff size={16} aria-hidden /> : <Pin size={16} aria-hidden />,
          onSelect: () => void flow.togglePin({ id, pinned }),
        },
        {
          key: 'rename',
          label: t('conversations.rename.action'),
          icon: <Pencil size={16} aria-hidden />,
          onSelect: () => flow.requestRename({ id, title }),
        },
        {
          key: 'delete',
          label: t('conversations.delete.action'),
          icon: <Trash2 size={16} aria-hidden />,
          destructive: true,
          onSelect: () => flow.requestDelete({ id, title }),
        },
      ]}
    />
  );
}
